import { createContext, useCallback, useEffect, useState } from "react";
import axios from "axios";
import { googleMapApiKey } from "../components/map/ProvidersLocationMap";
import useProfileContext from "../hooks/useProfileContext";

const AddressContext = createContext({});

export const AddressProvider = ({ children }) => {
  const { profileCoordinate } = useProfileContext();
  const [currentCoordinate, setCurrentCoordinate] = useState({
    lat: 0,
    lng: 0,
  });
  const [address, setAddress] = useState("");
  const [suggestAddress, setSuggestAddress] = useState("");
  const [suggestPostalCode, setSuggestPostalCode] = useState("");
  const [suggestCordinate, setSuggestCordinate] = useState({ lat: 0, lng: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [postalCode, setPostalCode] = useState("");
  const [geolocationError, setGeolocationError] = useState(null);

  // Get address from coordinates using Google Geocoding API
  const getAddress = useCallback(async (latitude, longitude) => {
    if (!latitude || !longitude) {
      console.warn("Invalid coordinates provided.");
      return;
    }

    const apiKey = googleMapApiKey;
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`;

    try {
      setIsLoading(true);
      const response = await axios.get(url);

      if (response.data.status !== "OK" || !response.data.results?.length) {
        console.error(
          "Geocoding API error:",
          response.data.error_message || "No results found"
        );
        setAddress("Address not found");
        setPostalCode("");
        return;
      }

      const result = response.data.results[0];
      const postalComponent = result.address_components?.find((comp) =>
        comp.types?.includes("postal_code")
      );

      setAddress(result.formatted_address || "Address not found");
      setPostalCode(postalComponent?.long_name || "");
    } catch (error) {
      console.error("Geocoding API request failed:", error);
      setAddress("Could not fetch address");
      setPostalCode("");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch current coordinates with retry logic
  const getCurrentCoordinate = useCallback(() => {
    if (!navigator.geolocation) {
      console.warn("Geolocation is not supported by this browser.");
      setGeolocationError("Geolocation not supported");
      // Fallback to profileCoordinate or default
      const fallbackCoords = profileCoordinate;
      setCurrentCoordinate(fallbackCoords);
      getAddress(fallbackCoords.lat, fallbackCoords.lng);
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0, // Force fresh location
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setCurrentCoordinate({ lat: latitude, lng: longitude });
        getAddress(latitude, longitude);
        setGeolocationError(null);
      },
      (error) => {
        console.error("Geolocation error:", error);
        setGeolocationError(error.message);
        // Fallback to profileCoordinate or default
        const fallbackCoords = profileCoordinate;
        setCurrentCoordinate(fallbackCoords);
        getAddress(fallbackCoords.lat, fallbackCoords.lng);
      },
      options
    );
  }, [getAddress, profileCoordinate]);

  // Initialize location fetch on mount
  useEffect(() => {
    getCurrentCoordinate();
  }, [getCurrentCoordinate]);

  return (
    <AddressContext.Provider
      value={{
        currentCoordinate,
        setCurrentCoordinate,
        address,
        isLoading,
        setAddress,
        postalCode,
        setPostalCode,
        suggestAddress,
        setSuggestAddress,
        suggestPostalCode,
        setSuggestPostalCode,
        suggestCordinate,
        setSuggestCordinate,
        geolocationError,
      }}
    >
      {children}
    </AddressContext.Provider>
  );
};

export default AddressContext;
